import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import cosmaLogo from "assets/images/logos/Cosmaupload.png";
import { useMaterialUIController, setLayout } from "context";

const TYPES = [
  {
    type: "single-user",
    path: "/auth/register/single-user",
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#7b5ea7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
    label: "Single User",
    tagline: "Individual access to COSMA portal & app",
    bullets: ["Full portal access", "Progress reports & charts", "Game activity tracking", "Personal dashboard"],
    accent: "#7b5ea7",
    badge: "Most Popular",
  },
  {
    type: "organisation",
    path: "/auth/register/organisation",
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#1a7f5a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="9" width="18" height="12" rx="2" />
        <path d="M8 9V7a4 4 0 0 1 8 0v2" />
        <circle cx="12" cy="14" r="2" />
      </svg>
    ),
    label: "Organisation",
    tagline: "Manage members, doctors & patients",
    bullets: ["Multi-user management", "Doctor & patient portals", "Organisation-wide reports", "Admin dashboard"],
    accent: "#1a7f5a",
    badge: null,
  },
  {
    type: "community",
    path: "/auth/register/community",
    icon: (
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c47800" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3" />
        <circle cx="16" cy="8" r="3" />
        <path d="M2 20c0-3 2.7-5 6-5" />
        <path d="M13 15c3.3 0 6 2 6 5" />
        <path d="M9 15c1.1 0 2.1.2 3 .6" />
      </svg>
    ),
    label: "Community",
    tagline: "Free tier — play COSMA brain games",
    bullets: ["Free to join", "Play all COSMA games", "Portal access with subscription", "App-based experience"],
    accent: "#c47800",
    badge: "Free",
  },
];

const S = {
  root: {
    fontFamily: "'Inter', sans-serif",
    background: `url(${bgImage}) center center / cover no-repeat`,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
  },
  logo: { marginBottom: "24px", textAlign: "center" },
  logoImg: { height: "44px" },
  h2: { fontSize: "1.9rem", fontWeight: 800, color: "#fff", textAlign: "center", margin: "0 0 8px 0", textShadow: "0 2px 16px rgba(0,0,0,0.25)" },
  sub: { color: "rgba(255,255,255,0.85)", fontSize: "1rem", textAlign: "center", marginBottom: "36px" },
  grid: { display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center", width: "100%", maxWidth: "960px" },
  card: (accent, hover) => ({
    background: "#fff",
    borderRadius: "20px",
    padding: "32px 28px",
    width: "260px",
    flex: "1 1 240px",
    maxWidth: "300px",
    cursor: "pointer",
    border: `2px solid ${hover ? accent : "rgba(0,0,0,0.08)"}`,
    boxShadow: hover ? `0 8px 32px ${accent}33` : "0 2px 16px rgba(0,0,0,0.10)",
    transition: "all .22s",
    transform: hover ? "translateY(-4px)" : "none",
    position: "relative",
  }),
  iconWrap: (accent) => ({
    width: "64px", height: "64px", borderRadius: "16px",
    background: `${accent}15`, display: "flex", alignItems: "center",
    justifyContent: "center", marginBottom: "18px",
  }),
  cardLabel: { fontSize: "1.2rem", fontWeight: 800, color: "#1a0a2e", marginBottom: "6px" },
  cardTagline: { fontSize: "0.83rem", color: "#7a6895", marginBottom: "18px", lineHeight: 1.5 },
  bullet: (accent) => ({
    display: "flex", alignItems: "center", gap: "8px",
    fontSize: "0.82rem", color: "#3d2d5a", marginBottom: "8px",
  }),
  dot: (accent) => ({
    width: "7px", height: "7px", borderRadius: "50%",
    background: accent, flexShrink: 0,
  }),
  chooseBtn: (accent) => ({
    marginTop: "22px", width: "100%", padding: "12px",
    borderRadius: "10px", border: "none", cursor: "pointer",
    background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
    color: "#fff", fontWeight: 700, fontSize: "0.9rem",
    fontFamily: "'Inter', sans-serif",
    boxShadow: `0 4px 16px ${accent}44`,
    transition: "all .2s",
  }),
  badge: (accent) => ({
    position: "absolute", top: "16px", right: "16px",
    background: accent, color: "#fff",
    fontSize: "0.7rem", fontWeight: 800, padding: "3px 10px",
    borderRadius: "100px", textTransform: "uppercase", letterSpacing: "0.06em",
  }),
  links: { marginTop: "28px", fontSize: "0.84rem", color: "rgba(255,255,255,0.8)", textAlign: "center" },
  a: { color: "#fff", fontWeight: 700, textDecoration: "underline" },
};

function RegisterSelect() {
  const navigate = useNavigate();
  const [, uiDispatch] = useMaterialUIController();
  const [hovered, setHovered] = React.useState(null);

  useEffect(() => {
    setLayout(uiDispatch, "page");
  }, []);

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={S.root}>
        <div style={S.logo}>
          <img src={cosmaLogo} alt="COSMA" style={S.logoImg} />
        </div>
        <h2 style={S.h2}>Create Your Account</h2>
        <p style={S.sub}>Choose the account type that fits you best.</p>

        <div style={S.grid}>
          {TYPES.map((t) => (
            <div
              key={t.type}
              style={S.card(t.accent, hovered === t.type)}
              onClick={() => navigate(t.path)}
              onMouseEnter={() => setHovered(t.type)}
              onMouseLeave={() => setHovered(null)}
            >
              {t.badge && <span style={S.badge(t.accent)}>{t.badge}</span>}
              <div style={S.iconWrap(t.accent)}>{t.icon}</div>
              <div style={S.cardLabel}>{t.label}</div>
              <div style={S.cardTagline}>{t.tagline}</div>
              {t.bullets.map((b, i) => (
                <div key={i} style={S.bullet(t.accent)}>
                  <span style={S.dot(t.accent)} />
                  {b}
                </div>
              ))}
              <button style={S.chooseBtn(t.accent)}>Get Started →</button>
            </div>
          ))}
        </div>

        <p style={S.links}>
          Already have an account?{" "}
          <a href="/auth/login" style={S.a}>Sign In →</a>
        </p>
      </div>
    </>
  );
}

export default RegisterSelect;
